// ============================================
// 2026-09-14｜D 阶段：部件导出为 GLB 资产
// 对标 Blender「导出选中对象」/ Unity Prefab 提取 / Three.js Editor「Export Object」
// 用途：把组合模型内部的小部件导出为独立 GLB 文件 → 可在其他场景用「导入 3D 模型」复用
// ============================================

import * as THREE from 'three';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { message } from 'antd';
import { modelRefsRegistry } from '../stores/modelRefsRegistry';
import { deepCloneObject } from '../components/scene/subObjectUtils';

export function exportSubObjectAsGlb(modelId: string, uuid: string, name: string): void {
  const handle = modelRefsRegistry.get(modelId);
  const obj = handle?.group?.getObjectByProperty('uuid', uuid);
  if (!obj) {
    message.error('导出失败：部件不存在');
    return;
  }
  // 骨骼部件限制：GLTFExporter 导出 SkinnedMesh 需骨骼绑定完整，易异常——本期禁用并提示
  let hasSkinned = false;
  obj.traverse((c: any) => {
    if (c?.isSkinnedMesh) hasSkinned = true;
  });
  if (hasSkinned) {
    message.warning('该部件含骨骼动画，暂不支持导出为资产');
    return;
  }

  // 独立材质副本（不影响原部件；GLTFExporter 会连同贴图一起打包）
  const clone = deepCloneObject(obj);
  // ============================================
  // 2026-09-14 修复（导出→导入同场景"缩小 10-20 倍"）：
  // 原"携带世界变换"方案（导出根带 worldScale 如 0.05）——导出物忠实于"导出时刻"的源场景视觉，
  // 但源模型 scale 后续变化（如 0.05→1）后再对比，就会显得"被缩小"（实为导出时刻的 0.05 缩放固化在根上）。
  // 改为主流"烘焙变换"方案（Blender Apply Transform 语义）：把每个 Mesh 的**世界矩阵固化进几何顶点**，
  // 整棵树 local 变换归一 → 导出物 = 导出时刻的源视觉尺寸（1:1），任意场景导入一致、再次导出稳定。
  // ============================================
  try {
    // 1) 源/副本 Mesh 配对（traverse 顺序一致），逐个把世界矩阵烘焙进（克隆后的）几何
    const srcMeshes: THREE.Mesh[] = [];
    obj.traverse((c: any) => {
      if (c.isMesh && c.geometry) srcMeshes.push(c as THREE.Mesh);
    });
    const cloneMeshes: THREE.Mesh[] = [];
    clone.traverse((c: any) => {
      if (c.isMesh && c.geometry) cloneMeshes.push(c as THREE.Mesh);
    });
    srcMeshes.forEach((sm, i) => {
      const cm = cloneMeshes[i];
      if (!cm || !cm.geometry) return;
      sm.updateWorldMatrix(true, false);
      const world = sm.matrixWorld.clone();
      // 独立几何副本（deepCloneObject 共享 geometry——直接 applyMatrix4 会污染源部件与其它实例）
      cm.geometry = cm.geometry.clone();
      cm.geometry.applyMatrix4(world);
    });
    // 2) 整棵树 local 变换归零（几何已含世界变换，节点保持单位变换）
    clone.traverse((c: any) => {
      if (c.position?.set) c.position.set(0, 0, 0);
      if (c.quaternion?.identity) c.quaternion.identity();
      if (c.scale?.set) c.scale.set(1, 1, 1);
    });
    // 3) 居中贴地：几何已含"源场景世界坐标"（如工厂模型坐标在数万处）——若不处理，
    //    导入新场景（position 0）后模型会出现在很远的地方。将几何平移：水平居中 + 底面贴地（y=0）。
    clone.updateMatrixWorld(true);
    const bakeBox = new THREE.Box3().setFromObject(clone);
    if (!bakeBox.isEmpty()) {
      const bakeCenter = bakeBox.getCenter(new THREE.Vector3());
      const dx = -bakeCenter.x;
      const dy = -bakeBox.min.y;
      const dz = -bakeCenter.z;
      clone.traverse((c: any) => {
        if (c.isMesh && c.geometry) {
          c.geometry.translate(dx, dy, dz);
        }
      });
    }
  } catch (err) {
    console.warn('[exportSubObject] bake world transform failed, fallback to local transform:', err);
  }

  const exporter = new GLTFExporter();
  exporter.parse(
    clone,
    (result) => {
      try {
        const blob =
          result instanceof ArrayBuffer
            ? new Blob([result], { type: 'model/gltf-binary' })
            : new Blob([JSON.stringify(result)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(name || '部件').replace(/[\\/:*?"<>|]/g, '_')}.glb`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 3000);
        message.success(`已导出「${name}」为 GLB——可在其他场景用「导入 3D 模型」复用`);
      } catch {
        message.error('导出失败（文件生成异常）');
      }
    },
    (err) => {
      console.error('[exportSubObject] GLTFExporter error:', err);
      message.error('导出失败（部件含不支持的特性）');
    },
    { binary: true },
  );
}
